import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { getCroppedBlob } from '../utils/cropImage';

/**
 * AvatarCropDialog — lets the user position & zoom their face into a square
 * before upload (fixes auto-center cropping). Exports a downscaled JPEG blob.
 */
export const AvatarCropDialog = ({ imageSrc, onCancel, onSave, saving }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);

  const onComplete = useCallback((_area, px) => setAreaPixels(px), []);

  const handleSave = async () => {
    if (!areaPixels) return;
    const blob = await getCroppedBlob(imageSrc, areaPixels);
    onSave(blob);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="avatar-crop-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={saving ? undefined : onCancel} />
      <div className="relative w-full max-w-sm bg-surface border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <p className="font-secondary font-bold text-white">Frame your photo</p>
          <p className="text-xs text-zinc-500">Drag to position, use the slider to zoom.</p>
        </div>
        <div className="relative h-72 bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>
        <div className="px-4 py-3 space-y-3">
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-primary"
            data-testid="avatar-crop-zoom"
            aria-label="Zoom"
          />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving} data-testid="avatar-crop-cancel">
              Cancel
            </Button>
            <Button className="flex-1 hpme-btn-primary" onClick={handleSave} disabled={saving || !areaPixels} data-testid="avatar-crop-save">
              {saving ? 'Saving…' : 'Save photo'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarCropDialog;
